import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './api';
import type {
  ApiUser,
  AllowlistEntry,
  AllowlistImportInput,
  Album,
  Photo,
  AlbumCreateInput,
  AlbumUpdateInput,
  PhotoUpdateInput,
} from '@w6/shared';

export function useMe() {
  return useQuery({
    queryKey: ['me'],
    queryFn: () => apiFetch<ApiUser>('/me'),
  });
}

export function useAnnouncement() {
  return useQuery({
    queryKey: ['announcement'],
    queryFn: () => apiFetch<{ announcementHtml: string }>('/announcement'),
  });
}

export function useUpdateAnnouncement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (html: string) =>
      apiFetch('/announcement', { method: 'PATCH', body: JSON.stringify({ html }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['announcement'] }),
  });
}

export function useAllowlist() {
  return useQuery({
    queryKey: ['allowlist'],
    queryFn: () => apiFetch<AllowlistEntry[]>('/admin/allowlist'),
  });
}

export function useImportAllowlist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (entries: AllowlistImportInput['entries']) =>
      apiFetch<{ added: number; updated: number }>('/admin/allowlist/import', {
        method: 'POST',
        body: JSON.stringify({ entries }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['allowlist'] }),
  });
}

export function useDeleteAllowlistEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (email: string) =>
      apiFetch(`/admin/allowlist/${encodeURIComponent(email)}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['allowlist'] }),
  });
}

export function useAlbums() {
  return useQuery({
    queryKey: ['albums'],
    queryFn: () => apiFetch<Album[]>('/albums'),
  });
}

export function useAlbum(id: string | undefined) {
  return useQuery({
    queryKey: ['album', id],
    queryFn: () => apiFetch<{ album: Album; photos: Photo[] }>(`/albums/${id}`),
    enabled: !!id,
  });
}

export function useCreateAlbum() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AlbumCreateInput) =>
      apiFetch<Album>('/albums', { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['albums'] }),
  });
}

export function useUpdateAlbum(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AlbumUpdateInput) =>
      apiFetch<Album>(`/albums/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['albums'] });
      qc.invalidateQueries({ queryKey: ['album', id] });
    },
  });
}

export function useDeleteAlbum() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/albums/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['albums'] }),
  });
}

export function useUploadPhotos(albumId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      files,
      description,
      photographer,
    }: {
      files: File[];
      description?: string;
      photographer?: string;
    }) => {
      const fd = new FormData();
      files.forEach((f) => fd.append('photos', f));
      if (description) fd.append('description', description);
      if (photographer) fd.append('photographer', photographer);
      return apiFetch<Photo[]>(`/albums/${albumId}/upload`, { method: 'POST', body: fd });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['album', albumId] });
      qc.invalidateQueries({ queryKey: ['albums'] });
    },
  });
}

export function useUpdatePhoto(albumId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ photoId, input }: { photoId: string; input: PhotoUpdateInput }) =>
      apiFetch<Photo>(`/albums/${albumId}/photos/${photoId}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['album', albumId] });
    },
  });
}

export function useDeletePhoto(albumId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (photoId: string) =>
      apiFetch(`/albums/${albumId}/photos/${photoId}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['album', albumId] });
      qc.invalidateQueries({ queryKey: ['albums'] });
    },
  });
}

export async function fetchDownloadUrl(albumId: string, photoId: string): Promise<string> {
  const data = await apiFetch<{ url: string; expiresAt: string }>(
    `/albums/${albumId}/photos/${photoId}/download`,
  );
  return data.url;
}
